import * as React from 'react'
import * as LodashFP from 'lodash/fp'

import { Box, Typography, styled } from '@design/pt-ui'
import { IStringAnyMap } from '../types/map'
import { FormBlueprint } from '../interfaces/components/dynamic-form'
import { getBlueprintAtPath } from '../lib/dynamic-form/helpers'

const joinPaths = (path1: string, path2: string) => {
  if (path1 === '') {
    return `${path2}`
  } else if (path2 === '') {
    return `${path2}`
  } else {
    return `${path1}.${path2}`
  }
}

const Column = styled('div')(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column',
  padding: theme.spacing(2),
  paddingTop: 0,
}))

const HeaderItem = styled('div')(({ theme }) => ({
  display: 'flex',
  paddingRight: theme.spacing(2),
  marginRight: theme.spacing(2),
  alignItems: 'center',
  fontSize: '14px',
  borderBottom: '1px solid #e8e8e8',
  paddingBottom: theme.spacing(1),
  paddingTop: theme.spacing(1),
}))

const OptionItem = styled('div')(({ theme }) => ({
  marginRight: theme.spacing(2),
}))

interface Props {
  title?: string
  indent?: number
  path?: string
  data: IStringAnyMap
  blueprint: FormBlueprint
}

function DataTableForBlueprint(props: Props) {
  const nicer = (val: any) => {
    const str = `${val}`
    return str === 'performance_advertising'
      ? 'PIA Advertising'
      : LodashFP.startCase(LodashFP.camelCase(str))
  }

  const shouldSkip = (value: any) => {
    if (LodashFP.isArray(value) && value.length === 0) {
      return true
    }

    if (LodashFP.isPlainObject(value) && Object.keys(value).length === 0) {
      return true
    }

    if (LodashFP.isString(value) && value.length === 0) {
      return true
    }

    return value == null
  }

  const renderKey = (key: string, path: string) => {
    const blueprint = getBlueprintAtPath(props.blueprint, path)

    return (
      <Typography
        variant="subtitle1"
        align="left"
        sx={{
          fontWeight: 700,
          marginRight: 2,
          fontSize: '16px',
          color: 'primary.dark',
        }}>
        {blueprint ? blueprint.label : nicer(key)}
      </Typography>
    )
  }

  const renderValue = (value: any, path: string) => {
    const blueprintAtPath = getBlueprintAtPath(props.blueprint, path)

    if (blueprintAtPath && blueprintAtPath.type === 'select') {
      const option = blueprintAtPath.meta.options.find(
        (option) => option.value === value,
      )
      if (option) {
        return (
          <OptionItem>
            <div>{option.label}</div>
          </OptionItem>
        )
      }
    }

    const potentialId = conditionallyRenderId(
      'cp-num-of-creatives-display',
      blueprintAtPath && blueprintAtPath.name === 'recommended_number',
    )

    return (
      <OptionItem id={potentialId}>
        <div>{nicer(value)}</div>
      </OptionItem>
    )
  }

  const conditionallyRenderId = (id: string, condition: boolean | null) => {
    return condition ? id : undefined
  }

  const renderArray = (
    key: string,
    value: any,
    indent: number,
    path: string,
  ) => {
    return (
      <Column key={key} sx={{ ml: `${indent * 10}px` }}>
        <HeaderItem>
          {renderKey(key, path)}
          {value.map((option: any, optionIndex: any) => {
            return (
              <React.Fragment key={optionIndex}>
                {renderValue(option, path)}
              </React.Fragment>
            )
          })}
        </HeaderItem>
      </Column>
    )
  }

  const { title, data, indent = 0, path = '' } = props

  return (
    <>
      {title ? (
        <Box
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            color: 'primary.dark',
            borderBottom: '1px solid #e8e8e8',
            padding: '16px 16px 16px 0px',
            marginRight: '16px',
            marginLeft: `${(indent - 2) * 10 + 16}px`,
          }}>
          <div>{title}:</div>
        </Box>
      ) : null}

      {Object.keys(data).map((key: string) => {
        const value = data[key]
        const fullPath = joinPaths(path, key)
        const blueprint = getBlueprintAtPath(props.blueprint, fullPath)
        if (shouldSkip(value)) {
          return <React.Fragment key={key} />
        }

        if (LodashFP.isArray(value) && value.length > 0) {
          return renderArray(key, value, indent, fullPath)
        }

        if (LodashFP.isPlainObject(value)) {
          return (
            <DataTableForBlueprint
              indent={indent + 2}
              key={key}
              title={blueprint ? blueprint.label : nicer(key)}
              data={value}
              path={fullPath}
              blueprint={props.blueprint}
            />
          )
        }

        return (
          <Column key={key} sx={{ ml: `${indent * 10}px` }}>
            <HeaderItem>
              {renderKey(key, fullPath)}
              {renderValue(value, fullPath)}
            </HeaderItem>
          </Column>
        )
      })}
    </>
  )
}

export default DataTableForBlueprint
